import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { Moment } from 'moment';
import { CopyOutlined, PlusOutlined } from '@ant-design/icons';
import callApi from '../../Api/config';
import { AFFILIATE_PARTNERS, COUNTRY } from '../../constants/EntityName';
import AbstractTabs, { TabActiosEventType, TabActiosProps } from '../Entity/AbstractTabs';
import Accordian from '../../components/Accordian/Accordian';
import GenericAccordian from '../../components/GenericAccordian/GenericAccordian';
import ConfirmationModal from '../../components/ConfirmationModal';
import B2BAffiliatePartnerEditTab from './B2BAffiliatePartnerEditTab';
import DefaultTableHeader from '../Entity/AbstractLanding/DefaultTableHeader';
import { TabContent } from '../../utils/forms/FormInterface';
import { joinDialAndLocal, PHONE_DIAL_CODES } from '../../constants/phoneDialCodes';
import {
  formatPartnerReferralDomain,
  getPartnerRootDomainSuffix,
} from '../../utils/partnerSubdomain';
import { buildPartnerBrandingPayload } from '../../utils/partnerBranding';
import PartnerLogoUpload from '../../components/PartnerLogoUpload';
import PartnerProgramServicesEditor from '../../components/PartnerProgramServicesEditor';
import type { PartnerProgramServiceFormValue } from '../../types/partnerProgramService';
import PartnerProgramFaqsEditor from '../../components/PartnerProgramFaqsEditor';
import { InvertFillButton } from '../../components/InvertFillButton';
import type { PartnerProgramFaqFormValue } from '../../types/partnerProgramFaq';
import { toastError, toastSuccess, toastWarning } from '../../service/Toast';
import '../../components/style.scss';
import './index.scss';

type CountryOption = {
  id?: string;
  name?: string;
  countryName?: string;
};

type AffiliatePartner = {
  id: string;
  partnerId: string;
  name: string;
  companyName?: string;
  websiteUrl?: string;
  industry?: string;
  email: string;
  mobile: string;
  contactPerson?: string;
  country?: string;
  referralCode: string;
  referralLink: string;
  subdomain?: string;
  branding?: {
    logos?: {
      partner?: string;
    };
  };
  commissionType: 'percentage' | 'flat';
  commissionValue: number;
  status: 'active' | 'inactive';
  totalOrders: number;
  totalEarnings: number;
  createdAt: string;
  kycStatus?: 'pending' | 'done' | 'deactivated';
  kycDueAt?: string;
  kycUploadedCount?: number;
  kycRequiredCount?: number;
};

type AddressForm = {
  street?: string;
  apartment?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
};

type PaymentForm = {
  zelle?: string;
  venmo?: string;
  upi?: string;
  bankBeneficiaryName?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankIfscSwift?: string;
};

type CreatePartnerFormValues = {
  name: string;
  companyName?: string;
  websiteUrl?: string;
  industry?: string;
  email: string;
  phoneDialCode: string;
  mobileNumber: string;
  country?: string;
  status: 'active' | 'inactive';
  commissionType: 'percentage' | 'flat';
  commissionValue: number;
  subdomain?: string;
  partnerLogoUrl?: string;
  partnerProgramServices?: PartnerProgramServiceFormValue[];
  partnerProgramFaqs?: PartnerProgramFaqFormValue[];
  address: AddressForm;
  payment: PaymentForm;
};

const { Text } = Typography;

const formatReferralDomain = (partner: Pick<AffiliatePartner, 'subdomain' | 'referralLink'>) => {
  if (partner.subdomain) {
    return formatPartnerReferralDomain(partner.subdomain);
  }
  const link = (partner.referralLink || '').replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  if (link && !link.includes('/ref/')) {
    return link.replace(/\/+$/, '');
  }
  return partner.referralLink || '';
};

const B2BAffiliateProgram = () => {
  const [form] = Form.useForm<CreatePartnerFormValues>();
  const [partners, setPartners] = useState<AffiliatePartner[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeKey, setActiveKey] = useState<string>('0');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AffiliatePartner | null>(null);
  const [showFormComponent, setShowFormComponent] = useState<boolean>(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<any>({});
  /** Open partner edit tabs (same pattern as Document Order — one closable tab per row). */
  const [editTabContent, setEditTabContent] = useState<TabContent>([]);

  const commissionTypeWatched = Form.useWatch('commissionType', form);

  const fetchCountries = async () => {
    try {
      const res: any = await callApi(COUNTRY, 'GET', null, {
        page: 1,
        limit: 300,
      });
      // `callApi` wraps backend response into: { data: <backendResponse>, status, statusCode }
      // `GET /admin/countries` returns: { isSuccess, statusCode, message, data: string[] }
      // So country list usually lives at `res.data.data` and may already be an array of strings.
      const rows = res?.data?.data || res?.data?.data?.results || [];
      const mapped = (Array.isArray(rows) ? rows : [])
        .map((item: any) => {
          if (typeof item === 'string') return item;
          return item?.name || item?.countryName || item?.country;
        })
        .filter(Boolean);
      setCountries(mapped);
    } catch (error) {
      setCountries([]);
    }
  };

  const fetchPartners = async (searchQuery = search, filterObj = filters) => {
    setLoading(true);
    try {
      const { country, status, startDate, endDate } = filterObj;
      const params: any = {
        page: 1,
        limit: 100,
        search: searchQuery || undefined,
        country: country || undefined,
        status: status || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };

      const res: any = await callApi(AFFILIATE_PARTNERS, 'GET', null, params);
      if (res?.status) {
        const results = res?.data?.data?.results || [];
        setPartners(results);
      } else {
        setPartners([]);
        if (res?.data?.message) {
          toastWarning(res.data.message);
        }
      }
    } catch (error: any) {
      toastError(error?.message || 'Failed to fetch partners');
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [search, filters]);

  const filteredPartners = partners;

  const resetFilters = () => {
    setSearch('');
    setFilters({});
  };

  const resetAndOpenCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      status: 'active',
      commissionType: 'percentage',
      commissionValue: 10,
      subdomain: '',
      partnerLogoUrl: '',
      partnerProgramServices: [],
      partnerProgramFaqs: [],
      phoneDialCode: '+1',
      mobileNumber: '',
      address: {},
      payment: {},
    });
    setShowFormComponent(true);
    setActiveKey('a');
  };

  const openEdit = (partner: AffiliatePartner) => {
    const key = partner.partnerId;
    if (editTabContent.some(t => String(t.key) === String(key))) {
      setActiveKey(String(key));
      return;
    }
    setEditTabContent(prev => [
      ...prev,
      {
        key,
        tabTitle: partner.partnerId,
        content: (
          <B2BAffiliatePartnerEditTab
            initialPartner={partner}
            countries={countries}
            onClose={() => {
              setEditTabContent(c => c.filter(t => String(t.key) !== String(key)));
              setActiveKey('0');
            }}
            onSaved={() => fetchPartners(search.trim())}
          />
        ),
      },
    ]);
    setActiveKey(String(key));
  };

  const handleCreatePartner = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const mobile = joinDialAndLocal(values.phoneDialCode || '+1', values.mobileNumber || '');
      const payload = {
        name: values.name,
        companyName: values.companyName,
        websiteUrl: values.websiteUrl?.trim(),
        industry: values.industry?.trim(),
        email: values.email,
        mobile,
        country: values.country,
        status: values.status,
        commissionType: values.commissionType,
        commissionValue: Number(values.commissionValue || 0),
        subdomain: values.subdomain?.trim().toLowerCase(),
        branding: buildPartnerBrandingPayload({
          partnerLogoUrl: values.partnerLogoUrl,
          displayName: values.companyName || values.name,
          partnerProgramServices: values.partnerProgramServices,
          partnerProgramFaqs: values.partnerProgramFaqs,
        }),
        address: {
          street: values.address?.street,
          apartment: values.address?.apartment,
          city: values.address?.city,
          state: values.address?.state,
          zip: values.address?.zip,
          country: values.address?.country,
        },
        paymentDetails: {
          zelle: values.payment?.zelle,
          venmo: values.payment?.venmo,
          upi: values.payment?.upi,
          bank: {
            beneficiaryName: values.payment?.bankBeneficiaryName,
            bankName: values.payment?.bankName,
            accountNumber: values.payment?.bankAccountNumber,
            ifscSwift: values.payment?.bankIfscSwift,
          },
        },
      };

      const res: any = await callApi(AFFILIATE_PARTNERS, 'POST', payload);
      if (!res?.status) {
        const errorMessage =
          res?.data?.message || res?.data?.error?.message || 'Failed to create partner';
        toastError(errorMessage);
        // Even when creation fails (e.g. duplicate email), refresh table to
        // show already-created partners immediately.
        fetchPartners(search.trim());
        return;
      }

      toastSuccess('Partner created successfully');
      setShowFormComponent(false);
      setActiveKey('0');
      fetchPartners(search.trim());
    } catch (error: any) {
      if (error?.errorFields) return;
      toastError(error?.message || 'Failed to create partner');
    } finally {
      setSubmitting(false);
    }
  };

  const copyReferralLink = async (referralLink: string) => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toastSuccess('Referral link copied');
    } catch (error) {
      toastError('Unable to copy referral link');
    }
  };

  const handleDeletePartner = async (partner: AffiliatePartner) => {
    try {
      const encodedPartnerId = encodeURIComponent(partner.partnerId);
      const res: any = await callApi(`${AFFILIATE_PARTNERS}/${encodedPartnerId}`, 'DELETE', null);

      if (!res?.status) {
        toastError(res?.data?.message || 'Failed to delete partner');
        return;
      }

      toastSuccess('Partner deleted successfully');

      setEditTabContent(c => c.filter(t => String(t.key) !== String(partner.partnerId)));
      if (activeKey === String(partner.partnerId)) {
        setActiveKey('0');
      }

      fetchPartners(search.trim());
    } catch (error: any) {
      toastError(error?.message || 'Failed to delete partner');
    }
  };

  const requestDeletePartner = (partner: AffiliatePartner) => {
    setDeleteTarget(partner);
    setDeleteModalOpen(true);
  };

  const confirmDeletePartner = async () => {
    if (!deleteTarget) return;
    try {
      await handleDeletePartner(deleteTarget);
    } finally {
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  const columns: any[] = [
    {
      title: 'Partner ID',
      dataIndex: 'partnerId',
      key: 'partnerId',
      width: 130,
      render: (value: string, row: AffiliatePartner) => (
        <Button
          type="link"
          style={{
            padding: 0,

            lineHeight: '18px',
            color: '#323232',
            fontSize: '16px',
          }}
          onClick={() => openEdit(row)}
        >
          <span style={{ textDecoration: 'underline' }}>{value}</span>
        </Button>
      ),
    },
    {
      title: 'Partner Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (value: string, row: AffiliatePartner) => (
        <Button
          type="link"
          style={{ fontSize: '16px', fontWeight: 400, color: '#323232' }}
          onClick={() => openEdit(row)}
        >
          <span style={{ textDecoration: 'underline' }}>{value}</span>
        </Button>
      ),
    },
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 170,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 180,
      render: (value: string) => value || '--',
    },
    {
      title: 'Commission',
      key: 'commission',
      width: 120,
      render: (_: unknown, row: AffiliatePartner) => {
        const v = Number(row.commissionValue ?? 0);
        if (row.commissionType === 'percentage') {
          const rounded = Math.round(v * 100) / 100;
          const label = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
          return `${label}%`;
        }
        return `$${v.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`;
      },
    },

    {
      title: 'KYC Status',
      dataIndex: 'kycStatus',
      key: 'kycStatus',
      width: 130,
      render: (value: string) => {
        const v = (value || 'pending').toLowerCase();
        if (v === 'done') return <Tag color="green">KYC Done</Tag>;
        if (v === 'deactivated') return <Tag color="red">Deactivated</Tag>;
        return <Tag color="gold">Pending</Tag>;
      },
    },
    {
      title: 'Referral Domain',
      dataIndex: 'subdomain',
      key: 'referralDomain',
      width: 280,
      render: (_: string, row: AffiliatePartner) => {
        const domain = formatReferralDomain(row);
        return (
          <Space>
            <Text style={{ maxWidth: 220 }} ellipsis>
              {domain}
            </Text>
            <Button type="link" size="small" onClick={() => copyReferralLink(`https://${domain}`)}>
              <CopyOutlined />
            </Button>
          </Space>
        );
      },
    },
    {
      title: 'Performance',
      key: 'performance',
      width: 140,
      render: (_: unknown, row: AffiliatePartner) => (
        <Space direction="vertical" size={0}>
          <Text strong>${Number(row.totalEarnings || 0).toLocaleString()}</Text>
          <Text type="secondary">{Number(row.totalOrders || 0)} orders</Text>
        </Space>
      ),
    },
    {
      title: 'Joined',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (value: string) => (value ? new Date(value).toLocaleDateString() : '--'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (value: string) =>
        value === 'active' ? <Tag color="green">active</Tag> : <Tag color="default">inactive</Tag>,
    },
  ];

  const closeCreateTab = () => {
    setShowFormComponent(false);
    setActiveKey('0');
  };

  const updateFormVisibility = (
    action: TabActiosProps,
    targetKey?: string | TabActiosEventType
  ) => {
    if (action !== 'remove') return;
    const k = targetKey != null ? String(targetKey) : '';
    if (k === 'a') {
      closeCreateTab();
      return;
    }
    if (k && k !== '0') {
      setEditTabContent(prev => prev.filter(item => String(item.key) !== k));
      setActiveKey('0');
    }
  };

  return (
    <AbstractTabs
      activeKey={activeKey}
      entityName="Partner user management"
      updateFormVisibility={updateFormVisibility}
      onTabClick={(key: string) => setActiveKey(key)}
      secondTabTitle="Create Partner"
      showFormComponent={showFormComponent}
      content={editTabContent}
      formComponent={
        <GenericAccordian>
          <div className="affiliate-partner-edit">
            <Form form={form} layout="horizontal" className="affiliate-partner-edit__form">
              <div style={{ marginBottom: 12 }}>
                <Accordian title="Partner Information">
                  <Form.Item
                    name="name"
                    label="Partner Name"
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    rules={[{ required: true, message: 'Partner name is required' }]}
                  >
                    <Input placeholder="Enter partner name" />
                  </Form.Item>
                  <Form.Item
                    name="companyName"
                    label={<div className="leftOffset">Company Name</div>}
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    colon={false}
                  >
                    <Input placeholder="Enter company name" />
                  </Form.Item>
                  <Form.Item
                    name="websiteUrl"
                    label={<div className="leftOffset">Website URL</div>}
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    colon={false}
                  >
                    <Input placeholder="Enter website URL" />
                  </Form.Item>
                  <Form.Item
                    name="industry"
                    label={<div className="leftOffset">Industry</div>}
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    colon={false}
                  >
                    <Input placeholder="Enter industry" />
                  </Form.Item>
                  <Form.Item
                    name="country"
                    label="Origin Country"
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    colon={false}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select country"
                      dropdownClassName="select-checkbox-dropdown"
                      options={countries.map(c => ({ label: c, value: c }))}
                    />
                  </Form.Item>
                  <Form.Item
                    name="email"
                    label="Email ID"
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    rules={[
                      { required: true, message: 'Email is required' },
                      { type: 'email', message: 'Invalid email' },
                    ]}
                  >
                    <Input placeholder="Enter email address" />
                  </Form.Item>
                  <Form.Item
                    label={<div className="leftOffset">Mobile Number</div>}
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    colon={false}
                  >
                    <Space.Compact style={{ width: '100%' }}>
                      <Form.Item
                        name="phoneDialCode"
                        noStyle
                        initialValue="+1"
                        rules={[{ required: true, message: 'Code is required' }]}
                      >
                        <Select
                          showSearch
                          optionFilterProp="label"
                          style={{ width: 160 }}
                          dropdownClassName="select-checkbox-dropdown"
                          options={PHONE_DIAL_CODES.map(d => ({
                            label: d.label,
                            value: d.value,
                          }))}
                        />
                      </Form.Item>
                      <Form.Item
                        name="mobileNumber"
                        noStyle
                        rules={[{ required: true, message: 'Mobile number is required' }]}
                      >
                        <Input
                          style={{ width: 'calc(100% - 160px)' }}
                          placeholder="Mobile number"
                        />
                      </Form.Item>
                    </Space.Compact>
                  </Form.Item>
                  <Form.Item
                    name="status"
                    label="Status"
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    rules={[{ required: true, message: 'Status is required' }]}
                  >
                    <Radio.Group>
                      <Radio value="active">Active</Radio>
                      <Radio value="inactive">Inactive</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Accordian>
              </div>

              <div style={{ marginBottom: 12 }}>
                <Accordian title="Address Section">
                  <Form.Item
                    name={['address', 'street']}
                    label="Address Line 1"
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Input placeholder="Enter street address" />
                  </Form.Item>
                  <Form.Item
                    name={['address', 'apartment']}
                    label={<div className="leftOffset">Address Line 2</div>}
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    colon={false}
                  >
                    <Input placeholder="Enter apartment, suite, etc. (optional)" />
                  </Form.Item>
                  <Form.Item
                    name={['address', 'city']}
                    label="City"
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Input placeholder="Enter city" />
                  </Form.Item>
                  <Form.Item
                    name={['address', 'state']}
                    label="State"
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Input placeholder="Enter state or province" />
                  </Form.Item>
                  <Form.Item
                    name={['address', 'country']}
                    label="Country"
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select country"
                      dropdownClassName="select-checkbox-dropdown"
                      options={countries.map(c => ({ label: c, value: c }))}
                    />
                  </Form.Item>
                  <Form.Item
                    name={['address', 'zip']}
                    label="Zip/Postal Code"
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Input placeholder="Enter ZIP or postal code" />
                  </Form.Item>
                </Accordian>
              </div>

              <div style={{ marginBottom: 12 }}>
                <Accordian title="Affiliate Settings">
                  <Form.Item
                    name="commissionType"
                    label="Commission Type"
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    rules={[{ required: true, message: 'Commission type is required' }]}
                  >
                    <Radio.Group>
                      <Radio value="percentage">Percentage (% of order amount)</Radio>
                      <Radio value="flat">Fixed amount (USD per qualifying order)</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item
                    name="commissionValue"
                    label={
                      commissionTypeWatched === 'flat'
                        ? 'Fixed commission (USD)'
                        : 'Commission rate (%)'
                    }
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    rules={[
                      { required: true, message: 'Commission value is required' },
                      {
                        validator: async (_, value) => {
                          if (value === null || value === undefined || value === '') {
                            return Promise.reject(new Error('Commission value is required'));
                          }
                          const n = Number(value);
                          if (Number.isNaN(n) || n < 0) {
                            return Promise.reject(new Error('Enter zero or a positive number'));
                          }
                          if (commissionTypeWatched === 'percentage' && n > 100) {
                            return Promise.reject(
                              new Error('Percentage must be between 0 and 100')
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    extra={
                      commissionTypeWatched === 'flat'
                        ? 'Flat dollar amount earned per order (not a percent of the order).'
                        : 'Example: 10 means 10% of the order amount. Must be 0–100.'
                    }
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={commissionTypeWatched === 'percentage' ? 100 : undefined}
                      step={commissionTypeWatched === 'percentage' ? 0.1 : 0.01}
                      precision={2}
                      addonAfter={commissionTypeWatched === 'flat' ? undefined : '%'}
                      placeholder={commissionTypeWatched === 'flat' ? '0.00' : '0–100'}
                    />
                  </Form.Item>
                  {/* <Text type="secondary">
                    Partner ID is auto-generated after creation. Set the referral domain now; it
                    cannot be changed later.
                  </Text> */}
                  <Form.Item
                    name="subdomain"
                    label="Referral Domain"
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    style={{ marginTop: 10 }}
                    rules={[
                      { required: true, message: 'Referral domain is required' },
                      {
                        pattern: /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/,
                        message: 'Use lowercase letters, numbers, and hyphens only',
                      },
                    ]}
                    normalize={value => (value || '').toLowerCase().replace(/[^a-z0-9-]/g, '')}
                  >
                    <Input
                      addonAfter={getPartnerRootDomainSuffix()}
                      placeholder="Create your referral domain"
                    />
                  </Form.Item>
                </Accordian>
              </div>

              <div style={{ marginBottom: 12 }}>
                {/* <Accordian title="Page Customize"> */}
                <div style={{ marginBottom: 12 }}>
                  <Accordian title="Partner logo">
                    <Form.Item
                      name="partnerLogoUrl"
                      wrapperCol={{ span: 8, offset: 4 }}
                      labelCol={{ span: 4 }}
                      labelAlign="left"
                    >
                      <PartnerLogoUpload />
                    </Form.Item>
                  </Accordian>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <Accordian title="Partner Program Services">
                    <PartnerProgramServicesEditor />
                  </Accordian>
                </div>
                <div>
                  <Accordian title="Partner Program FAQs">
                    <PartnerProgramFaqsEditor />
                  </Accordian>
                </div>
                {/* </Accordian> */}
              </div>

              <div style={{ marginBottom: 12 }}>
                <Accordian title="Payment Information (optional)">
                  <Form.Item
                    name={['payment', 'zelle']}
                    label={<div className="leftOffset">Zelle ID</div>}
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    colon={false}
                  >
                    <Input placeholder="Enter Zelle ID" />
                  </Form.Item>
                  <Form.Item
                    name={['payment', 'venmo']}
                    label={<div className="leftOffset">Venmo ID</div>}
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    colon={false}
                  >
                    <Input placeholder="Enter Venmo ID" />
                  </Form.Item>
                  <Form.Item
                    name={['payment', 'upi']}
                    label={<div className="leftOffset">UPI ID</div>}
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    colon={false}
                  >
                    <Input placeholder="Enter UPI ID" />
                  </Form.Item>
                </Accordian>
              </div>
              <div style={{ marginBottom: 12 }}>
                <Accordian title="Bank Information">
                  <Form.Item
                    name={['payment', 'bankBeneficiaryName']}
                    label={<div className="leftOffset">Beneficiary Name</div>}
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    colon={false}
                  >
                    <Input placeholder="Enter beneficiary name" />
                  </Form.Item>
                  <Form.Item
                    name={['payment', 'bankName']}
                    label={<div className="leftOffset">Bank Name</div>}
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    colon={false}
                  >
                    <Input placeholder="Enter bank name" />
                  </Form.Item>
                  <Form.Item
                    name={['payment', 'bankAccountNumber']}
                    label={<div className="leftOffset">Account Number</div>}
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    colon={false}
                  >
                    <Input placeholder="Enter account number" />
                  </Form.Item>
                  <Form.Item
                    name={['payment', 'bankIfscSwift']}
                    label={<div className="leftOffset">Routing / IFSC / SWIFT</div>}
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    colon={false}
                  >
                    <Input placeholder="Enter routing/IFSC/SWIFT code" />
                  </Form.Item>
                </Accordian>
              </div>
            </Form>

            <div className="affiliate-partner-edit__actions">
              <InvertFillButton variant="white" onClick={closeCreateTab}>
                Cancel
              </InvertFillButton>
              <InvertFillButton
                variant="blue"
                disabled={submitting}
                onClick={handleCreatePartner}
              >
                {submitting ? 'Creating...' : 'Create Partner'}
              </InvertFillButton>
            </div>
          </div>
        </GenericAccordian>
      }
    >
      <ConfirmationModal
        open={deleteModalOpen}
        message="Are you sure you want to delete this partner?"
        onConfirm={confirmDeletePartner}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeleteTarget(null);
        }}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteTarget(null);
        }}
      />
      <div
        className="landing-container"
        style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '20px 0' }}
      >
        <DefaultTableHeader
          headerName="Partner Program"
          totalCount={filteredPartners.length}
          countText="Partners"
          setSearchQuery={setSearch}
          setPhoneSearchQuery={() => {}}
          onAdd={resetAndOpenCreate}
          addName="Partner"
          entityName={AFFILIATE_PARTNERS}
          showFilter={true}
          filters={filters}
          filterQuery={setFilters}
        />

        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredPartners}
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          bordered
        />
      </div>
    </AbstractTabs>
  );
};

export default B2BAffiliateProgram;
